import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { FileUpload, FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { TimelineModule } from 'primeng/timeline';
import { ToastModule } from 'primeng/toast';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { WorkflowPanelComponent } from '../../workflow/workflow-panel/workflow-panel.component';
import { ExpenseApproval } from '../expense-approval.model';
import { ExpenseAttachment } from '../expense-attachment.model';
import { Expense } from '../expense.model';
import { ExpenseService } from '../expense.service';
import { ExpensePaymentVoucherRequest, ExpenseSettlementSummary } from '../expense.service';
import { BankAccountService } from '../../accounts/bank-account/bank-account.service';
import { BankAccount } from '../../accounts/bank-account/bank-account.model';
import { ChartOfAccountsService } from '../../chart-of-accounts/chart-of-accounts.service';
import { ChartOfAccounts } from '../../chart-of-accounts/chart-of-accounts.model';

@Component({
    selector: 'app-view-expense',
    standalone: true,
    templateUrl: './view-expense.component.html',
    styleUrls: ['./view-expense.component.css'],
    imports: [
        CommonModule,
        RouterModule,
        FormsModule,
        ReactiveFormsModule,
        ToastModule,
        ProgressBarModule,
        BreadcrumbModule,
        TagModule,
        TextareaModule,
        ButtonModule,
        ChipModule,
        TableModule,
        TimelineModule,
        DialogModule,
        TabsModule,
        ConfirmDialogModule,
        FileUploadModule,
        PageMetricStripComponent,
        PageDetailHeroComponent,
        WorkflowPanelComponent
    ],
    providers: [
        MessageService,
        ConfirmationService,
        ExpenseService,
        SessionStorageService
    ]
})
export class ViewExpenseComponent implements OnInit, OnDestroy {
    private readonly ngZone = inject(NgZone);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly tabKeys = ['overview', 'items', 'approvals', 'attachments'] as const;
    @ViewChild('fileUploader') fileUploader?: FileUpload;

    expense?: Expense;
    settlement?: ExpenseSettlementSummary;
    canCreateVoucher = false;
    paymentDialogVisible = false;
    paymentSaving = false;
    paymentAmount = 0;
    paymentMode: ExpensePaymentVoucherRequest['paymentMode'] = 'CASH';
    cashAccountId?: number;
    fromBankAccountId?: number;
    cashAccounts: ChartOfAccounts[] = [];
    companyBankAccounts: BankAccount[] = [];
    isLoading = false;
    activeTabIndex = 0;
    displayApprovalDialog = false;
    approvalComments = '';
    currentAction: 'Approved' | 'Rejected' | '' = '';

    breadcrumbItems: MenuItem[] = [
        { label: 'Finance', routerLink: '/expense' },
        { label: 'Expenses', routerLink: '/expense' },
        { label: 'Expense Details' }
    ];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

    canEditExpense = false;
    canApproveExpense = false;
    canRejectExpense = false;
    canUploadAttachments = false;
    canDeleteAttachments = false;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private confirmationService: ConfirmationService,
        private expenseService: ExpenseService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService,
        private permissionService: PermissionService,
        private bankAccountService: BankAccountService,
        private chartOfAccountsService: ChartOfAccountsService
    ) {}

    ngOnInit(): void {
        this.canEditExpense = this.permissionService.hasPermission(app_permissions.expense.records.edit);
        this.canCreateVoucher = this.permissionService.hasPermission(app_permissions.accounts.voucher.create);
        this.canApproveExpense = this.permissionService.hasPermission(app_permissions.expense.approval.approve);
        this.canRejectExpense = this.permissionService.hasPermission(app_permissions.expense.approval.reject);
        this.canUploadAttachments = this.permissionService.hasPermission(app_permissions.expense.attachments.upload);
        this.canDeleteAttachments = this.permissionService.hasPermission(app_permissions.expense.attachments.delete);

        this.route.paramMap
            .pipe(takeUntil(this.destroy$))
            .subscribe(params => {
                const expenseId = Number(params.get('id'));
                if (expenseId) {
                    this.getExpenseDetails(expenseId);
                    return;
                }

                this.messageService.add({ severity: 'error', summary: 'Expense Details', detail: 'Expense ID was not provided.' });
                this.router.navigate(['/expense']);
            });

        this.route.queryParamMap
            .pipe(takeUntil(this.destroy$))
            .subscribe(params => {
                this.activeTabIndex = this.resolveTabIndex(params.get('tab'));
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private syncView(work?: () => void): void {
        this.ngZone.run(() => {
            work?.();
            this.cdr.detectChanges();
        });
    }

    onTabChange(event: string | number): void {
        this.activeTabIndex = Number(event);
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: this.tabKeys[this.activeTabIndex] || this.tabKeys[0] },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    getExpenseDetails(id: number): void {
        this.isLoading = true;

        forkJoin({
            expense: this.expenseService.getExpenseById(id)
        }).pipe(takeUntil(this.destroy$))
            .subscribe({
                next: ({ expense }) => {
                    this.syncView(() => {
                        this.expense = expense;
                        if (expense.status === 'Posted' || expense.status === 'Closed') this.loadSettlement(id);
                        this.breadcrumbItems = [
                            { label: 'Finance', routerLink: '/expense' },
                            { label: 'Expenses', routerLink: '/expense' },
                            { label: expense.expenseNumber || expense.description || `Expense #${id}` }
                        ];
                        this.isLoading = false;
                    });
                },
                error: () => {
                    this.syncView(() => {
                        this.isLoading = false;
                        this.messageService.add({ severity: 'error', summary: 'Expense Details', detail: 'Expense details could not be loaded.' });
                    });
                    this.router.navigate(['/expense']);
                }
            });
    }

    getMetrics(): PageMetric[] {
        const activeExpense = this.expense;
        return [
            { label: 'Total amount', value: this.formatCurrency(activeExpense?.totalAmount || 0, activeExpense?.currencyCode || 'SAR'), tone: 'info' },
            ...(this.settlement ? [
                { label: 'Paid', value: this.formatCurrency(this.settlement.paidAmount, this.settlement.currencyCode), tone: 'success' as const },
                { label: 'Open', value: this.formatCurrency(this.settlement.openAmount, this.settlement.currencyCode), tone: 'warn' as const }
            ] : []),
            { label: 'Tax amount', value: this.formatCurrency(this.getTotalTaxAmount(activeExpense), activeExpense?.currencyCode || 'SAR') },
            { label: 'Attachments', value: activeExpense?.attachments?.length || 0, tone: 'success', hint: 'Receipts and PDFs' },
            { label: 'Approvals', value: activeExpense?.approvals?.length || 0, tone: 'warn', hint: 'Workflow steps' }
        ];
    }

    getStatusSeverity(status?: string): string {
        switch (status) {
            case 'Approved':
            case 'Posted':
            case 'Closed':
                return 'success';
            case 'Pending Approval':
            case 'Submitted':
            case 'Under Validation':
                return 'warning';
            case 'Rejected':
            case 'Refused':
                return 'danger';
            case 'Draft':
                return 'info';
            default:
                return 'secondary';
        }
    }

    getTotalTaxAmount(expense?: Expense): number {
        return expense?.items?.reduce((sum, item) => sum + (item.taxAmount || 0), 0) || 0;
    }

    getDetailSubtitle(): string {
        const activeExpense = this.expense;
        if (!activeExpense) {
            return '';
        }

        const submittedBy = activeExpense.submittedBy?.name || 'No submitter recorded';
        const client = this.getClientDisplayName(activeExpense);
        return `${submittedBy} | ${client}`;
    }

    getClientDisplayName(expense?: Expense): string {
        return expense?.crmDisplayName
            || expense?.crmClient?.companyName
            || expense?.crmClient?.customerNameLocal
            || expense?.partyDisplayName
            || expense?.vendor?.name
            || 'No party';
    }

    getContactDisplayName(expense?: Expense): string {
        return expense?.crmContactDisplayName
            || expense?.crmClientContact?.name
            || expense?.crmContactEmail
            || expense?.crmContactPhone
            || expense?.partyContactDisplayName
            || '-';
    }

    editExpense(): void {
        if (this.expense?.expenseId && this.canEditExpense && this.expense.status === 'Draft') {
            this.router.navigate(['/expense/edit', this.expense.expenseId]);
        }
    }

    private loadSettlement(expenseId: number): void {
        this.expenseService.getSettlement(expenseId).pipe(takeUntil(this.destroy$)).subscribe({
            next: settlement => this.syncView(() => this.settlement = settlement),
            error: error => this.messageService.add({ severity: 'error', summary: 'Settlement',
                detail: error?.error?.message || 'Could not load expense payments.' })
        });
    }

    openPaymentDialog(): void {
        if (!this.expense?.expenseId || !this.settlement || !this.canCreateVoucher || this.settlement.openAmount <= 0) return;
        const companyId = this.expense.owningCompany?.id;
        if (!companyId) return;
        this.paymentAmount = this.settlement.openAmount;
        this.paymentMode = 'CASH';
        this.cashAccountId = undefined;
        this.fromBankAccountId = undefined;
        forkJoin({
            accounts: this.chartOfAccountsService.getAllChartOfAccountsByCompany(companyId, false),
            banks: this.bankAccountService.getBankAccounts('Company', companyId, '', 0, 1000)
        }).pipe(takeUntil(this.destroy$)).subscribe({
            next: ({ accounts, banks }) => this.syncView(() => {
                const currency = this.settlement?.currencyCode?.toUpperCase();
                const parentIds = new Set(accounts.map(account => account.parentAccount?.accountId));
                const flattened = (items: ChartOfAccounts[]): ChartOfAccounts[] => items.flatMap(item =>
                    [item, ...flattened(item.childrenAccounts || [])]);
                this.cashAccounts = flattened(accounts).filter(account => account.accountId && !parentIds.has(account.accountId)
                    && account.accountType?.code?.toUpperCase() === 'CASH' && account.isActive && !account.deprecated
                    && (!account.currencyCode || account.currencyCode.toUpperCase() === currency));
                this.companyBankAccounts = (banks.items || []).filter(bank => bank.currencyCode?.toUpperCase() === currency);
                this.paymentDialogVisible = true;
            }),
            error: () => this.messageService.add({ severity: 'error', summary: 'Payment accounts', detail: 'Could not load payment accounts.' })
        });
    }

    savePaymentVoucher(): void {
        if (!this.expense?.expenseId || !this.settlement || this.paymentSaving) return;
        if (!Number.isFinite(this.paymentAmount) || this.paymentAmount <= 0
            || Math.round(this.paymentAmount * 100) !== this.paymentAmount * 100
            || this.paymentAmount > this.settlement.openAmount
            || (this.paymentMode === 'CASH' ? !this.cashAccountId : !this.fromBankAccountId)) {
            this.messageService.add({ severity: 'error', summary: 'Payment voucher', detail: 'Enter a valid amount and payment account.' });
            return;
        }
        this.paymentSaving = true;
        this.expenseService.createPaymentVoucher(this.expense.expenseId, {
            amount: this.paymentAmount,
            paymentMode: this.paymentMode,
            cashAccountId: this.paymentMode === 'CASH' ? this.cashAccountId : undefined,
            fromBankAccountId: this.paymentMode === 'CASH' ? undefined : this.fromBankAccountId
        }).pipe(takeUntil(this.destroy$)).subscribe({
            next: result => this.syncView(() => {
                this.paymentSaving = false;
                this.paymentDialogVisible = false;
                this.router.navigate(['/accounts/enterprise/payables/generated-vouchers/view', result.voucherId]);
            }),
            error: error => this.syncView(() => {
                this.paymentSaving = false;
                this.messageService.add({ severity: 'error', summary: 'Payment voucher',
                    detail: error?.error?.message || 'Could not create payment voucher.' });
            })
        });
    }

    canShowApprovalAction(action: 'Approved' | 'Rejected'): boolean {
        const approval = this.currentUserApproval();
        if (!approval) {
            return false;
        }

        const isPending = approval.status?.toLowerCase() === 'pending';
        if (!isPending) {
            return false;
        }

        return action === 'Approved' ? this.canApproveExpense : this.canRejectExpense;
    }

    openApprovalDialog(action: 'Approved' | 'Rejected'): void {
        this.currentAction = action;
        this.approvalComments = '';
        this.displayApprovalDialog = true;
    }

    confirmApprovalAction(): void {
        const selectedApproval = this.currentUserApproval();
        if (!selectedApproval?.approvalId || !this.currentAction) {
            this.messageService.add({ severity: 'warn', summary: 'Approvals', detail: 'No approval step is available for the current user.' });
            return;
        }

        this.isLoading = true;
        this.expenseService.updateApprovalStatus(selectedApproval.approvalId, this.currentAction, this.approvalComments)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.syncView(() => {
                        this.displayApprovalDialog = false;
                        this.messageService.add({ severity: 'success', summary: 'Approvals', detail: `Expense ${this.currentAction.toLowerCase()} successfully.` });
                        this.currentAction = '';
                    });
                    if (this.expense?.expenseId) {
                        this.getExpenseDetails(this.expense.expenseId);
                    }
                },
                error: () => {
                    this.syncView(() => {
                        this.isLoading = false;
                        this.messageService.add({ severity: 'error', summary: 'Approvals', detail: `Expense could not be ${this.currentAction.toLowerCase()}.` });
                    });
                }
            });
    }

    onAttachmentsSelect(event: FileUploadHandlerEvent): void {
        const activeExpense = this.expense;
        if (!activeExpense?.expenseId || !activeExpense.owningCompany?.id) {
            return;
        }
        const expenseId = activeExpense.expenseId;
        const owningCompanyId = activeExpense.owningCompany.id;

        for (const file of event.files) {
            const reader = new FileReader();
            reader.onload = (loadEvent: ProgressEvent<FileReader>) => {
                const result = String(loadEvent.target?.result || '');
                const base64Content = result.includes(',') ? result.split(',')[1] : result;
                const attachment: ExpenseAttachment = {
                    fileName: file.name,
                    fileType: file.type,
                    fileSize: file.size,
                    filePath: '',
                    fileContent: base64Content
                };

                if (!activeExpense.attachments) {
                    activeExpense.attachments = [];
                }
                activeExpense.attachments.push(attachment);

                this.expenseService.createExpenseAttachment(expenseId, owningCompanyId, attachment)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: savedAttachment => {
                            const index = activeExpense.attachments?.findIndex(candidate => candidate.fileName === file.name && !candidate.attachmentId) ?? -1;
                            if (activeExpense.attachments && index >= 0) {
                                activeExpense.attachments[index] = savedAttachment;
                            }
                            this.messageService.add({ severity: 'success', summary: 'Attachment Uploaded', detail: `${file.name} saved successfully.` });
                        },
                        error: () => {
                            if (activeExpense.attachments) {
                                activeExpense.attachments = activeExpense.attachments.filter(candidate => candidate.fileName !== file.name || candidate.attachmentId);
                            }
                            this.messageService.add({ severity: 'error', summary: 'Attachment Upload', detail: `${file.name} could not be uploaded.` });
                        }
                    });
            };
            reader.readAsDataURL(file);
        }

        this.fileUploader?.clear();
    }

    downloadAttachment(attachment: ExpenseAttachment): void {
        if (!attachment.filePath) {
            this.messageService.add({ severity: 'warn', summary: 'Attachments', detail: 'This file is not ready for download yet.' });
            return;
        }

        try {
            const link = document.createElement('a');
            link.href = attachment.filePath;
            link.download = attachment.fileName;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch {
            this.messageService.add({ severity: 'error', summary: 'Attachments', detail: 'Download could not be started.' });
        }
    }

    confirmDeleteAttachment(attachment: ExpenseAttachment): void {
        if (!this.canDeleteAttachments) {
            return;
        }

        this.confirmationService.confirm({
            message: `Delete attachment <b>${attachment.fileName}</b>?`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => this.deleteAttachment(attachment)
        });
    }

    getApprovalStatusClass(status: string): string {
        switch (status?.toLowerCase()) {
            case 'approved':
                return 'bg-green-500';
            case 'pending':
                return 'bg-orange-500';
            case 'rejected':
            case 'refused':
                return 'bg-red-500';
            default:
                return 'bg-gray-400';
        }
    }

    getApprovalStatusIcon(status: string): string {
        switch (status?.toLowerCase()) {
            case 'approved':
                return 'pi pi-check';
            case 'pending':
                return 'pi pi-clock';
            case 'rejected':
            case 'refused':
                return 'pi pi-times';
            default:
                return 'pi pi-question';
        }
    }

    getApprovalTagSeverity(status: string): string {
        switch (status?.toLowerCase()) {
            case 'approved':
                return 'success';
            case 'pending':
                return 'warning';
            case 'rejected':
            case 'refused':
                return 'danger';
            default:
                return 'secondary';
        }
    }

    get workflowContext(): Record<string, unknown> {
        return {
            totalAmount: this.expense?.totalAmount ?? 0,
            currency: this.expense?.currencyCode ?? '',
            status: this.expense?.status ?? ''
        };
    }

    private currentUserApproval(): ExpenseApproval | undefined {
        const currentUserId = this.sessionStorageService.getCustomer()?.id;
        return this.expense?.approvals?.find(approval => approval.approver?.id === currentUserId);
    }

    private deleteAttachment(attachment: ExpenseAttachment): void {
        if (!attachment.attachmentId) {
            this.messageService.add({ severity: 'warn', summary: 'Attachments', detail: 'Unsaved attachments cannot be deleted from the server.' });
            return;
        }

        this.expenseService.deleteExpenseAttachment(attachment.attachmentId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    if (this.expense?.attachments) {
                        this.expense.attachments = this.expense.attachments.filter(candidate => candidate.attachmentId !== attachment.attachmentId);
                    }
                    this.messageService.add({ severity: 'success', summary: 'Attachments', detail: 'Attachment deleted successfully.' });
                },
                error: () => {
                    this.messageService.add({ severity: 'error', summary: 'Attachments', detail: 'Attachment could not be deleted.' });
                }
            });
    }

    private formatCurrency(value: number, currencyCode: string): string {
        return new Intl.NumberFormat('en-SA', {
            style: 'currency',
            currency: currencyCode || 'SAR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value || 0);
    }

    private resolveTabIndex(tab: string | null): number {
        const index = this.tabKeys.findIndex(value => value === (tab || '').toLowerCase());
        return index >= 0 ? index : 0;
    }
}
